import { useMemo, useState } from 'react';
import { LayoutChangeEvent, LayoutRectangle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { type DomainPropType } from 'victory-core';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryLine, VictoryTheme } from 'victory-native';
import { Box, HStack, useBreakpointValue, useTheme, VStack } from 'native-base';
import { flatten, isEmpty, map } from 'lodash';
import moment from 'moment';

import { Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { ChartDatum, SalesDaypart } from '../types';
import { BAR_COLORS, CHART_LAYOUT, LAYOUT } from '../consts';
import { useSalesLaborTracker } from '../context/sales-labor-tracker.context';

type SalesChartProps = {
  daypart?: Partial<SalesDaypart>;
};

export const SalesChart = ({ daypart }: SalesChartProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  const fontSize = useBreakpointValue({ base: 10, xl: 11 });
  const [layout, setLayout] = useState<Partial<LayoutRectangle>>();
  const displayForecastedSales = useSalesLaborTracker(_ => _.displayForecastedSales);
  const isOperatingHours = useSalesLaborTracker(_ => _.isOperatingHours);

  const title = !daypart?.partNumber
    ? `${t('sales_tracker:all_dayparts')} - ${t('sales_tracker:hourly')}`
    : `${translate(daypart.label, daypart.translations)} (${daypart.timeStart}) — ${t('sales_tracker:thirty_min_buckets')}`;

  const chart = useMemo(() => {
    const legends: { name: keyof typeof BAR_COLORS; label: string }[] = [
      { name: 'above', label: 'above_forecast' },
      { name: 'below', label: 'below_forecast' },
      { name: 'future', label: 'future' },
    ];

    const entries = map(daypart?.blocks, (block, i) => {
      let barColor = BAR_COLORS.future;
      if (block.hasActual) {
        barColor = (block.salesActual ?? 0) >= block.salesForecast ? BAR_COLORS.above : BAR_COLORS.below;
      }
      return {
        x: i + 1,
        barColor,
        timestamp: block.startTime.getTime(),
        y: block.hasActual ? block.salesActual ?? 0 : 0,
        forecast: block.salesForecast,
      } as ChartDatum;
    });

    const lineData = entries.map((_, i) => ({ x: i + 1, y: _.forecast }));
    const dataMax = Math.max(...flatten([entries.map(e => e.y), lineData.map(_ => _.y)]));
    const maxY = Math.ceil(dataMax / 100) * 100;
    // Dynamic bar width: fill ~60% of each slot, leaving 40% as gap
    const domain: DomainPropType = { x: [0.5, entries.length + 0.5], y: [0, maxY || 1000] };

    return {
      data: entries,
      domain,
      legends,
      lineData,
      tickValues: map(entries, _ => {
        return moment(_.timestamp).format(entries.length > 16 ? 'ha' : 'h:mm a');
      }),
    };
  }, [daypart, isOperatingHours]);

  const handleLayout = ({ nativeEvent: { layout: l } }: LayoutChangeEvent) => {
    setLayout({ width: l.width, height: l.height });
  };

  return (
    <VStack space={1} rounded={10} overflow="hidden" pt={2} borderWidth={1} bg="white">
      {/* Header */}
      <HStack alignItems="center" px={3}>
        <Box flex={1}>
          <Text size="sm" fontWeight={500} color="black">
            {title}
          </Text>
          {daypart?.status === 'future' && (
            <Text size="md" fontWeight={500} color="gray.500">
              {t('sales_tracker:forecast_only')}
            </Text>
          )}
        </Box>

        <HStack space={3} alignItems="center">
          {chart.legends.map(_ => (
            <HStack key={`sales-tracker-chart.legends.${_.name}`} alignItems="center" space={1}>
              <Box w={3} h={3} rounded="sm" bg={BAR_COLORS[_.name]} />
              <Text size={{ base: 'xs', xl: 'sm' }} fontWeight={500} color="gray.600">
                {t(`sales_tracker:${_.label}`)}
              </Text>
            </HStack>
          ))}
          {displayForecastedSales && (
            <HStack alignItems="center" space={1}>
              <Box w={5} h="2px" borderStyle="dashed" borderWidth={1} borderColor="gray.400" />
              <Text size={{ base: 'xs', xl: 'sm' }} fontWeight={500} color="gray.600">
                {t('sales_tracker:forecast')}
              </Text>
            </HStack>
          )}
        </HStack>
      </HStack>

      {/* Chart */}
      <HStack w="full" h={CHART_LAYOUT.height} alignItems="flex-end" onLayout={handleLayout}>
        <Box w={LAYOUT.stickyLabelW} />
        {!!layout?.width && !isEmpty(chart.data) && (
          <Box flex={1}>
            {/* ── Main bar chart (no y-axis labels) ── */}
            <VictoryChart
              key={daypart?.startTime?.toISOString()}
              width={layout.width - LAYOUT.stickyLabelW}
              height={CHART_LAYOUT.height}
              padding={{ top: CHART_LAYOUT.paddingTop, bottom: CHART_LAYOUT.paddingBottom, left: 0, right: 0 }}
              domain={chart.domain}
              theme={VictoryTheme.material}
              standalone
            >
              <VictoryAxis
                dependentAxis
                tickCount={5}
                style={{
                  axis: { stroke: 'transparent' },
                  ticks: { stroke: 'transparent', size: 5 },
                  grid: { stroke: colors.gray[50], strokeWidth: 1, strokeDasharray: 0 },
                  tickLabels: { display: 'hidden' },
                }}
              />

              <VictoryBar
                alignment="middle"
                data={chart.data}
                // barRatio={0.4}
                barWidth={26}
                cornerRadius={{ top: 3 }}
                style={{
                  data: {
                    fill: ({ datum }: { datum?: ChartDatum }) => datum?.barColor ?? 'gray',
                  },
                }}
              />

              {/* X-axis with bar index labels */}
              {/*
              No need to show to enhance visibility of the sales tables
              <VictoryAxis
                tickValues={chart.tickValues}
                tickLabelComponent={<VictoryLabel dy={-2} />}
                style={{
                  axis: { stroke: colors.gray[100] },
                  ticks: { stroke: 'transparent', size: 5 },
                  tickLabels: { fontFamily: "'Inter', sans-serif", fontSize, fill: colors.gray[600], textAlign: 'center' },
                  grid: { stroke: 'transparent', strokeWidth: 1 },
                }}
              /> */}

              {displayForecastedSales && (
                <VictoryLine
                  data={chart.lineData}
                  x="x"
                  y="y"
                  style={{
                    data: {
                      stroke: colors.gray[400],
                      strokeWidth: 1.5,
                      strokeDasharray: '4,3',
                    },
                  }}
                  interpolation="monotoneX"
                />
              )}
            </VictoryChart>

            <Box position="absolute" left={`-${CHART_LAYOUT.yAxisW - 4}px`} top={0}>
              {/* ── Left panel: y-axis labels only ── */}
              <VictoryChart
                width={CHART_LAYOUT.yAxisW}
                height={CHART_LAYOUT.height}
                padding={{ top: CHART_LAYOUT.paddingTop, bottom: CHART_LAYOUT.paddingBottom, left: CHART_LAYOUT.yAxisW - 4, right: 0 }}
                domain={chart.domain}
                theme={VictoryTheme.material}
              >
                {/* Y-axis: labels only, everything else transparent */}
                <VictoryAxis
                  dependentAxis
                  tickCount={5}
                  tickFormat={(v: number) => (Number.isInteger(v) ? `$${v}` : '')}
                  style={{
                    axis: { stroke: colors.gray[100] },
                    ticks: { stroke: 'transparent', size: 0 },
                    grid: { stroke: colors.gray[50], strokeWidth: 1, strokeDasharray: 0 },
                    tickLabels: { fontFamily: "'Inter', sans-serif", fontSize, fill: colors.gray[600], textAnchor: 'end' },
                  }}
                />
              </VictoryChart>
            </Box>
          </Box>
        )}
      </HStack>
    </VStack>
  );
};
